import { useCallback, useEffect, useState } from "react";
import { api, type DeletionPreview, type ExportState } from "./api.ts";
import { ConfirmDialog } from "./Dialog.tsx";
import { lastDay } from "./format.ts";
import { usePersonEvents } from "./me-live.ts";
import { ReportThis } from "./ReportThis.tsx";
import { DangerAction } from "./SettingsGroup.tsx";
import { Button } from "./souvenir.ts";
import { ButtonLink } from "./ui.tsx";

// Your data, on the profile's card Your data and account (features/privacy-and-data.md, "The rights, as features"):
// Download your data (one file, prepared in a moment, there for 24 hours, once a day); and Delete my account, alone
// in the Danger zone card after it (a dialog saying exactly what happens to each of my trips and everything else,
// before anything does). Then the Terms and the Privacy policy, quietly, under the last card.

export const TERMS_URL = "https://tripmo.me/terms";
export const PRIVACY_URL = "https://tripmo.me/privacy";

const TIME = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });

// "today at 14:05", "Sun 19 Oct at 14:05".
function when(iso: string, now = new Date()): string {
  const at = new Date(iso);
  const sameDay = at.toDateString() === now.toDateString();
  return `${sameDay ? "today" : lastDay(iso, now)} at ${TIME.format(at)}`;
}

function size(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// The later of two things heard about my export: answers and live events can arrive in any order (the
// file can be ready, and its event here, before the request that asked for it has answered), and an
// export never goes back from ready to being prepared, nor to none.
const STAGE = { pending: 0, ready: 1, failed: 1, expired: 2 } as const;
export function later(prev: ExportState | null, next: ExportState): ExportState {
  if (!prev || prev.status === "none") return next;
  if (next.status === "none") return prev;
  if (prev.id !== next.id) return next.requested_at >= prev.requested_at ? next : prev;
  return STAGE[next.status] >= STAGE[prev.status] ? next : prev;
}

export function YourData() {
  const [state, set] = useState<ExportState | null>(null);
  const setState = useCallback((next: ExportState) => set((prev) => later(prev, next)), []);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  usePersonEvents({ data: setState });
  useEffect(() => {
    api
      .dataExport()
      .then(setState)
      .catch(() => {});
  }, [setState]);
  // While it's being prepared, the page also asks now and then: the live event saying it's ready can be
  // missed (the connection dropped or not open yet), and the file shouldn't wait for a reload.
  const preparing = state?.status === "pending";
  useEffect(() => {
    if (!preparing) return;
    const timer = setInterval(() => {
      api
        .dataExport()
        .then(setState)
        .catch(() => {});
    }, 3000);
    return () => clearInterval(timer);
  }, [preparing, setState]);

  const ask = async () => {
    setAsking(true);
    setError(null);
    try {
      setState(await api.requestExport());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAsking(false);
    }
  };

  const ready = state && state.status === "ready" && state.url ? state : null;
  const pending = state?.status === "pending";
  const next = state && state.status !== "none" ? state.next_at : null;
  return (
    <div className="tm-settings-part">
      <p className="caption tm-muted">
        Everything Tripmo keeps about you, in one file (JSON): your account, the trips you own, what's yours in the
        trips you've joined, your purchases and your settings.
      </p>
      {ready ? (
        <div className="tm-data-ready">
          <ButtonLink variant="primary" size="sm" href={ready.url as string} className="tm-edge-start">
            Download your file{ready.size ? ` · ${size(ready.size)}` : ""}
          </ButtonLink>
          <p className="caption tm-muted" role="status">
            Ready until {when(ready.expires_at as string)}, and the links to your files inside it too. Then you can ask
            for a new one.
          </p>
        </div>
      ) : pending ? (
        <p className="callout tm-muted tm-data-pending" role="status">
          <span className="tm-data-spinner" aria-hidden="true" />
          Preparing your file… It's ready in a moment; you can leave this page.
        </p>
      ) : next ? (
        <p className="caption tm-muted">You can download your data once a day: the next one from {when(next)}.</p>
      ) : (
        <>
          {state?.status === "failed" && (
            <p className="callout tm-error" role="alert">
              Your file couldn't be prepared. Try again.
              <ReportThis />
            </p>
          )}
          <Button variant="secondary" size="sm" className="tm-edge-start" disabled={asking} onClick={ask}>
            {asking ? "Asking…" : "Download your data"}
          </Button>
        </>
      )}
      {error && (
        <p className="callout tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
    </div>
  );
}

export function DeleteAccount({ email }: { email: string }) {
  const [preview, setPreview] = useState<DeletionPreview | null>(null);
  const [opening, setOpening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    setOpening(true);
    setError(null);
    try {
      setPreview(await api.deletionPreview());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOpening(false);
    }
  };
  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.deleteAccount();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };

  return (
    <DangerAction
      what="Deleting your account takes the trips only you are in with it; the trips you share are handed over or left. You see what happens to each before anything does."
      action={opening ? "Checking your trips…" : "Delete my account"}
      disabled={opening}
      onClick={open}
    >
      {!preview && error && (
        <p className="callout tm-error" role="alert">
          {error}
          <ReportThis />
        </p>
      )}
      {preview && (
        <ConfirmDialog
          title="Delete your account?"
          action="Delete my account"
          doing="Deleting…"
          busy={busy}
          error={error}
          onConfirm={confirm}
          onCancel={() => {
            setPreview(null);
            setError(null);
          }}
        >
          <WhatHappens preview={preview} email={email} />
        </ConfirmDialog>
      )}
    </DangerAction>
  );
}

const names = (trips: { title: string }[]) => {
  const t = trips.map((x) => x.title);
  return t.length <= 1 ? (t[0] ?? "") : `${t.slice(0, -1).join(", ")} and ${t.at(-1)}`;
};

const PLAN: Record<string, string> = {
  yearly: "Frequent Flyer",
  lifetime: "Lifetime",
  comp_lifetime: "Lifetime",
  partner: "Partner",
};

// The seven steps, as they apply to this person, in plain words.
function WhatHappens({ preview, email }: { preview: DeletionPreview; email: string }) {
  const plans = [...new Set(preview.plans_ending.map((p) => PLAN[p.kind] ?? "plan"))];
  return (
    <>
      <p className="body">
        You're signed out everywhere, and your AIs can no longer reach Tripmo as you. Here's what happens to the rest:
      </p>
      <ul className="tm-dialog-list body">
        {preview.deleted_trips.length > 0 && (
          <li>
            {preview.deleted_trips.length === 1 ? "The trip" : "The trips"} only you are in,{" "}
            {names(preview.deleted_trips)}, {preview.deleted_trips.length === 1 ? "is" : "are"} deleted.
          </li>
        )}
        {preview.handed_over.map((t) => (
          <li key={t.id}>
            {t.title} goes to {t.to.name}, who has been in it longest. They're told.
          </li>
        ))}
        {preview.left.length > 0 && <li>You leave {names(preview.left)}.</li>}
        {preview.handed_over.length + preview.left.length > 0 && (
          <li>
            What you added to shared trips stays for the others, shown as from “a former member”, with no name, photo or
            email. To take something out, delete it first.
          </li>
        )}
        <li>
          Your ratings, notes, questions, answers, travel documents and their files, your location and photo, your
          notifications and your AI connections are deleted.
        </li>
        {preview.purchases > 0 && (
          <li>
            Your purchases are kept for 5 years, as accounting law requires, without your name or email
            {preview.stripe_customer ? ", and your customer record at Stripe is deleted" : ""}.
          </li>
        )}
        {plans.length > 0 && <li>Your {plans.join(" and ")} plan ends.</li>}
        {preview.unused_passes > 0 && (
          <li>
            {preview.unused_passes === 1
              ? "Your unused Trip Pass isn't"
              : `Your ${preview.unused_passes} unused Trip Passes aren't`}{" "}
            refunded by deleting: ask for a refund first.
          </li>
        )}
      </ul>
      <p className="body">
        Your account itself goes for good on {lastDay(preview.finishes_at)}. If this is a mistake, write to
        support@tripmo.me from {email} before then, and it can be undone. Want a copy first? Download your data.
      </p>
    </>
  );
}

// The Terms and the Privacy policy, at the end of the profile.
export function Legal() {
  return (
    <p className="caption tm-muted tm-legal">
      <a className="tm-legal-link" href={TERMS_URL}>
        Terms
      </a>
      <span aria-hidden="true"> · </span>
      <a className="tm-legal-link" href={PRIVACY_URL}>
        Privacy
      </a>
    </p>
  );
}
